/**
 * Defining the attributes a contact may carry.
 *
 * `GET /v1/custom-fields` had three callers and `POST` had none, so on any
 * install that was not seeded there was no way to create a definition at all —
 * and three shipped features quietly did nothing as a result. `ContactDrawer`
 * renders attribute inputs from these definitions, `ImportContacts` maps CSV
 * columns onto them, and `TemplateEditor` lists them as merge fields; with the
 * table empty all three render nothing, and `validateAttributes` then refuses
 * any attribute a contact might carry, so the column is unusable rather than
 * merely unpopulated.
 *
 * It stayed invisible because `demo` writes the definitions with raw SQL, so
 * the feature worked on every machine anybody looked at — the same mechanism
 * that hid the provider registry, where seeding a `dev` config meant local
 * sending took the one path that worked.
 *
 * IN SETTINGS RATHER THAN ON CONTACTS, and the capability is the argument. A
 * field definition is organization schema, not audience data, and
 * `custom_fields:manage` sits in the admin preset while Contacts is open to
 * marketing and support. On Contacts this would be invisible to most of the
 * people looking at that screen, which reads as a missing feature rather than
 * as a permission.
 */

import { useState } from "react";

import { api, ApiError } from "../api/client";
import { Icon } from "../components/Sprites";
import { useAsync } from "../hooks";
import { useSession } from "../session";
import type { CustomField, CustomFieldType } from "../api/types";

const TYPES: { id: CustomFieldType; label: string; hint: string }[] = [
  { id: "text", label: "Text", hint: "Any string." },
  { id: "number", label: "Number", hint: "Stored and compared as a number." },
  { id: "boolean", label: "Yes / no", hint: "True or false." },
  { id: "date", label: "Date", hint: "An ISO date." },
  { id: "enum", label: "Choice", hint: "One of a fixed set you list below." },
];

export function CustomFields() {
  const { orgId } = useSession();
  const list = useAsync<CustomField[]>(
    (signal) => api.get<CustomField[]>("/v1/custom-fields", { signal }),
    [orgId],
  );

  const fields = list.data ?? [];

  return (
    <div className="oc-panel">
      <div className="oc-panel__header row row--between">
        <div>
          <div className="oc-headline-md">Custom fields</div>
          <div className="oc-body-sm oc-text-secondary">
            The attributes a contact may carry. A contact can only hold a value under a key defined
            here — anything else is refused rather than stored.
          </div>
        </div>
        <button type="button" className="oc-btn oc-btn--ghost oc-btn--sm" onClick={list.reload}>
          <Icon name="refresh" size={12} />
          Refresh
        </button>
      </div>

      {list.error && (
        <div className="oc-panel__body">
          <p className="field-error">{list.error.message}</p>
        </div>
      )}

      {fields.length === 0 && !list.loading && (
        <div className="oc-panel__body">
          <div className="oc-empty">
            <div className="oc-empty__title">No custom fields yet</div>
            <div className="oc-empty__hint">
              Until one exists, contacts carry a name and their addresses and nothing else — and a
              CSV import has no column to map beyond those.
            </div>
          </div>
        </div>
      )}

      {fields.length > 0 && (
        <div className="oc-table field-table">
          <div className="oc-table__head" role="row">
            <span>Label</span>
            <span>Key</span>
            <span>Type</span>
            <span>Choices</span>
            <span className="oc-table__cell--right" />
          </div>
          <div className="oc-table__body">
            {fields.map((field) => (
              <FieldRow key={field.id} field={field} onChanged={list.reload} />
            ))}
          </div>
        </div>
      )}

      <AddField onAdded={list.reload} />
    </div>
  );
}

function FieldRow({ field, onChanged }: { field: CustomField; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remove = async () => {
    // What happens to the data is the thing somebody is deciding here, and it
    // is not what "delete" implies. The API leaves stored values in place —
    // rewriting every contact to strip an attribute is an expensive and
    // irreversible edit to customer data to trigger by removing a label. The
    // useful half is the other direction: recreate the definition and the
    // values are still there, still correct.
    const ok = window.confirm(
      `Remove the "${field.label}" field?\n\n` +
        `Values already stored on contacts are kept, not deleted. They stop being shown and ` +
        `stop being importable until a field with the key "${field.key}" exists again — at ` +
        `which point they reappear unchanged.`,
    );
    if (!ok) return;

    setBusy(true);
    setError(null);
    try {
      await api.delete(`/v1/custom-fields/${encodeURIComponent(field.id)}`);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not remove the field.");
      setBusy(false);
    }
  };

  return (
    <div className="oc-table__row" role="row">
      <span className="oc-truncate">{field.label}</span>
      <span className="oc-mono-sm oc-text-secondary oc-truncate">{field.key}</span>
      <span className="oc-text-secondary">
        {TYPES.find((t) => t.id === field.type)?.label ?? field.type}
      </span>
      <span className="oc-body-sm oc-text-secondary oc-truncate">
        {field.type === "enum" ? (field.options ?? []).join(", ") : "—"}
      </span>
      <span className="oc-table__cell--right">
        <button
          type="button"
          className="oc-btn oc-btn--ghost oc-btn--sm"
          disabled={busy}
          onClick={() => void remove()}
        >
          {busy ? "Removing…" : "Remove"}
        </button>
        {error && <div className="field-error">{error}</div>}
      </span>
    </div>
  );
}

function AddField({ onAdded }: { onAdded: () => void }) {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [key, setKey] = useState("");
  const [keyTouched, setKeyTouched] = useState(false);
  const [type, setType] = useState<CustomFieldType>("text");
  const [options, setOptions] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | Error | null>(null);

  // Suggested from the label until somebody edits it, then left alone. The key
  // is permanent and the label is not, so the two stop tracking each other the
  // moment the key is deliberate.
  const effectiveKey = keyTouched ? key : suggestKey(label);

  const reset = () => {
    setOpen(false);
    setLabel("");
    setKey("");
    setKeyTouched(false);
    setType("text");
    setOptions("");
    setError(null);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        key: effectiveKey,
        label: label.trim(),
        type,
      };
      if (type === "enum") {
        body.options = options
          .split(",")
          .map((o) => o.trim())
          .filter(Boolean);
      }
      await api.post("/v1/custom-fields", body);
      reset();
      onAdded();
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <div className="oc-panel__body">
        <button type="button" className="oc-btn oc-btn--sm" onClick={() => setOpen(true)}>
          <Icon name="plus" size={12} />
          Add a field
        </button>
      </div>
    );
  }

  const fieldError = error instanceof ApiError ? error.fields : {};
  const keyValid = /^[a-z][a-z0-9_]{0,62}$/.test(effectiveKey);

  return (
    <form className="oc-panel__body stack" onSubmit={(event) => void submit(event)}>
      <div className="row" style={{ gap: "var(--oc-space-3)", alignItems: "flex-start" }}>
        <label className="login__field grow">
          <span className="oc-label">Label</span>
          <input
            className="oc-input oc-input--sm"
            value={label}
            placeholder="Plan"
            aria-invalid={Boolean(fieldError["/label"])}
            onChange={(event) => setLabel(event.target.value)}
          />
        </label>

        <label className="login__field grow">
          <span className="oc-label">Key</span>
          <input
            className="oc-input oc-input--sm oc-mono-sm"
            value={effectiveKey}
            placeholder="plan"
            aria-invalid={Boolean(fieldError["/key"]) || (effectiveKey !== "" && !keyValid)}
            onChange={(event) => {
              setKeyTouched(true);
              setKey(event.target.value);
            }}
          />
          {/* Said where the key is typed, not after it is rejected: this is
              also the template variable name, which is the whole reason the
              character set is restricted. */}
          <span className="oc-body-sm oc-text-secondary">
            Also the merge field, written <code className="oc-mono-sm">{`{{.${effectiveKey || "key"}}}`}</code> in a
            template. Lowercase, digits and underscores; cannot be changed later.
          </span>
        </label>
      </div>

      <div className="row" style={{ gap: "var(--oc-space-3)", alignItems: "flex-start" }}>
        <label className="login__field" style={{ flex: "none", width: 200 }}>
          <span className="oc-label">Type</span>
          <select
            className="oc-input oc-input--sm"
            value={type}
            onChange={(event) => setType(event.target.value as CustomFieldType)}
          >
            {TYPES.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
          <span className="oc-body-sm oc-text-secondary">
            {TYPES.find((t) => t.id === type)?.hint}
          </span>
        </label>

        {type === "enum" && (
          <label className="login__field grow">
            <span className="oc-label">Choices</span>
            <input
              className="oc-input oc-input--sm"
              value={options}
              placeholder="free, pro, enterprise"
              aria-invalid={Boolean(fieldError["/options"])}
              onChange={(event) => setOptions(event.target.value)}
            />
            <span className="oc-body-sm oc-text-secondary">
              Comma separated. A contact may hold one of these and nothing else.
            </span>
          </label>
        )}
      </div>

      {error && <p className="field-error">{error.message}</p>}

      <div className="row" style={{ gap: "var(--oc-space-2)" }}>
        <button
          type="submit"
          className="oc-btn oc-btn--sm"
          disabled={saving || !label.trim() || !keyValid}
        >
          {saving ? "Saving…" : "Add field"}
        </button>
        <button type="button" className="oc-btn oc-btn--ghost oc-btn--sm" onClick={reset}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/** A label reduced to something the key pattern accepts. */
function suggestKey(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/^([0-9])/, "f$1")
    .slice(0, 63);
}
